'use client';

import { FullCycleStock, useToggleBoolean } from '@3fourteen/core';
import {
  ArrowLongDownIcon,
  ArrowLongUpIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/20/solid';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { BREAKPOINTS } from 'constants/breakpoints';
import { useSignedCloudfrontUrl } from 'hooks/useSignedCloudfrontUrl';
import { memo, useCallback, useMemo, useState } from 'react';

import {
  Button,
  DownloadButton,
  EyeIcon,
  FullCycleTableRow,
  TableSortDisplay,
} from 'components';

import { columns } from './columns';

interface FullCycleTableProps {
  data?: FullCycleStock[];
  csv?: string;
}

function FullCycleTable({ data, csv }: FullCycleTableProps) {
  const [showInactive, toggleInactive] = useToggleBoolean(false);
  const isSmallScreen = useMediaQuery(BREAKPOINTS.sm);
  const hideText = isSmallScreen ? 'Closed' : 'Hide All Closed';
  const showText = isSmallScreen ? 'Closed' : 'Show All Closed';

  const { url: csvUrl } = useSignedCloudfrontUrl(csv);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility] = useState<VisibilityState>({
    is_new: false,
    is_old: false,
  });

  const tableData = useMemo(() => {
    let fcStocks = [...(data ?? [])];

    if (!showInactive) {
      fcStocks = [...(data ?? [])].filter(({ inactive_date, is_old }) => !inactive_date || is_old);
    }

    return fcStocks.map(
      ({
        ticker,
        date,
        pct_change,
        sp500_change,
        sector,
        company_name,
        ranking,
        inactive_date,
        is_new,
        is_old,
      }) => ({
        date,
        ticker,
        pct_change,
        sp500_change,
        sector,
        company_name,
        ranking,
        inactive_date,
        is_new,
        is_old,
      })
    );
  }, [data, showInactive]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const sortedCol = sorting[0];
  const sortOption = sortedCol
    ? table.getColumn(sortedCol.id)?.columnDef.header as string
    : undefined;
  const sortOrder = sortedCol
    ? sortedCol.desc ? 'DESC' : 'ASC'
    : undefined;

  const clearSorting = useCallback(() => {
    setSorting([]);
  }, []);

  return (
    <div>
      <div className='flex items-center w-full'>
        <TableSortDisplay
          sortOption={sortOption ?? ''}
          sortOrder={sortOrder ?? 'ASC'}
          onResetSort={clearSorting}
          containerStyles={{
            marginRight: 8,
          }}
        />
        <div className='w-full md:w-auto flex justify-end md:justify-start p-4 md:p-0 md:pr-6'>
          <div className='mr-2'>
            <Button onClick={toggleInactive} className='btn-secondary btn-xs'>
              <EyeIcon isOpen={!showInactive} color='#254653' size={16} />
              <span className='inline-block ml-1.5'>{showInactive ? hideText : showText}</span>
            </Button>
          </div>
          {csvUrl && (
            <DownloadButton
              text={isSmallScreen ? 'CSV' : 'Download CSV'}
              href={csvUrl}
              download={`${csv}`}
              data-testid='download-fct-csv-btn'
            />
          )}
        </div>
      </div>
      <div className='border-b border-solid border-zinc-200 table-container'>
        <table className='bg-white w-full border-collapse border-spacing-0'>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className='border-y border-solid border-zinc-300'>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    data-testid={`${header.column.columnDef.header}-th`}
                    className='py-1.5 px-3 first:pl-4 md:first:pl-6 sticky top-[-1px] uppercase text-sm font-bold text-left whitespace-nowrap bg-teal-100'
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ cursor: 'pointer' }}>
                    <span className='flex items-center'>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <span className='ml-1'>
                        {{
                          asc: <ArrowLongUpIcon className='h-4 w-4' />,
                          desc: <ArrowLongDownIcon className='h-4 w-4' />,
                        }[header.column.getIsSorted() as string] ?? (
                          <ArrowsUpDownIcon className='h-4 w-4 opacity-30' />
                        )}
                      </span>
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody data-testid='full-cycle-tbody'>
            {table.getRowModel().rows.map((row) => {
              const inactiveRow = !!row.getValue('inactive_date');

              return <FullCycleTableRow key={row.id} row={row} inactiveRow={inactiveRow} />;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
export default memo(FullCycleTable);
